import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Footer: React.FC = () => {
  const [showImpressum, setShowImpressum] = useState(false);

  return (
    <>
      {/* Footer */}
      <footer 
        className="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-2 sm:py-3 px-3 sm:px-4 shadow-lg border-t border-gray-700 cursor-pointer"
        onClick={(e) => {
          // Nur neu laden wenn nicht auf den Impressum-Button geklickt wurde
          if (!(e.target as HTMLElement).closest('button')) {
            window.location.reload();
          }
        }}
      >
        <div className="max-w-4xl mx-auto flex justify-center items-center">
          <button
            onClick={() => setShowImpressum(true)}
            className="text-xs sm:text-sm text-gray-300 hover:text-white transition-colors duration-200 flex items-center gap-1 sm:gap-2 py-2 px-3 rounded-lg hover:bg-white/10 touch-manipulation min-h-[44px]"
          >
            <span className="text-sm sm:text-base">🧾</span>
            <span className="hidden sm:inline">Impressum & Datenschutz</span>
            <span className="sm:hidden">Impressum</span>
          </button>
        </div>
      </footer>

      {/* Impressum Modal */}
      <AnimatePresence key="impressum-modal">
        {showImpressum && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={() => setShowImpressum(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6 rounded-t-xl z-10">
                <div className="flex justify-between items-center">
                  <h1 className="text-lg sm:text-2xl font-bold">🧾 Impressum & Datenschutz</h1>
                  <button
                    onClick={() => setShowImpressum(false)}
                    className="text-white hover:text-gray-200 transition-colors duration-200 text-xl sm:text-2xl p-2 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-white/10"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
                {/* Impressum Section */}
                <section>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                    🧾 Impressum
                  </h2>
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
                    <p className="text-xs sm:text-sm text-gray-600 font-medium">Angaben gemäß § 5 TMG / § 18 MStV:</p>
                    <div className="space-y-1">
                      <p className="text-sm sm:text-base text-gray-800">Manuel Schweitzer</p>
                      <p className="text-sm sm:text-base text-gray-800">Rappacher Weg 46</p>
                      <p className="text-sm sm:text-base text-gray-800">63776 Mömbris</p>
                      <p className="text-sm sm:text-base text-gray-800">Deutschland</p>
                    </div>
                    <p className="text-sm sm:text-base text-gray-800">
                      <strong>E-Mail:</strong> schweitzermanuel@gmail.com
                    </p>
                    <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                      <p className="text-xs sm:text-sm text-gray-600 font-medium">Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV:</p>
                      <p className="text-sm sm:text-base text-gray-800">Manuel Schweitzer, Anschrift wie oben</p>
                    </div>
                    <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                      <p className="text-xs sm:text-sm text-gray-600">
                        <strong>Hinweis:</strong><br />
                        Dies ist ein nicht-kommerzielles, rein privates Webprojekt. Es besteht bisher keine Gewinnerzielungsabsicht.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Datenschutzerklärung Section */}
                <section>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                    🔐 Datenschutzerklärung
                  </h2>
                  <div className="space-y-3 sm:space-y-4">
                    <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                      <h3 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">1. Allgemeine Hinweise</h3>
                      <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
                        Der Schutz Ihrer persönlichen Daten ist mir sehr wichtig. Diese Webanwendung verarbeitet keinerlei personenbezogene Daten und verwendet keine Cookies, keine externen Tracking-Tools und keine eingebundenen Drittanbieterdienste wie Google Fonts oder Analysewerkzeuge.
                      </p>
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-3 sm:p-4">
                      <h3 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">Analyse mit GoAccess</h3>
                      <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
                        Zur Analyse des Web‑Traffics wird GoAccess (Version 1.8.1) eingesetzt. GoAccess wertet technisch bedingte Server‑Logdaten aus, um Besucherzahlen und Zugriffsmuster zu analysieren. IP‑Adressen werden dabei vor der Auswertung anonymisiert (Maskierung der letzten Oktette) und maximal 24 Stunden gespeichert; danach werden sie gelöscht oder anonymisiert. Die Auswertung dient ausschließlich der Betriebs‑ und Sicherheitsanalyse; eine Weitergabe an Dritte erfolgt nicht.
                      </p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                      <h3 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">2. Verantwortlicher</h3>
                      <p className="text-gray-700 text-xs sm:text-sm mb-2">Verantwortlich für die Datenverarbeitung auf dieser Website ist:</p>
                      <div className="space-y-1">
                        <p className="text-sm sm:text-base text-gray-800">Manuel Schweitzer</p>
                        <p className="text-sm sm:text-base text-gray-800">Rappacher Weg 46</p>
                        <p className="text-sm sm:text-base text-gray-800">63776 Mömbris</p>
                        <p className="text-sm sm:text-base text-gray-800">Deutschland</p>
                        <p className="text-sm sm:text-base text-gray-800"><strong>E-Mail:</strong> schweitzermanuel@gmail.com</p>
                      </div>
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-3 sm:p-4">
                      <h3 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">3. Keine Erhebung personenbezogener Daten</h3>
                      <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
                        Diese Webanwendung dient ausschließlich privaten, nicht-kommerziellen Zwecken. Es werden keine Nutzerdaten gespeichert oder verarbeitet. Es erfolgt keine Registrierung, keine Anmeldung, kein Tracking und keine Werbung.
                      </p>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                      <h3 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">4. Server-Logfiles (technisch bedingt)</h3>
                      <p className="text-gray-700 text-xs sm:text-sm mb-2 leading-relaxed">
                        Beim Aufruf dieser Seite über einen Webhoster oder Server werden durch diesen möglicherweise automatisch folgende Informationen protokolliert:
                      </p>
                      <ul className="text-gray-700 text-xs sm:text-sm space-y-1 ml-3 sm:ml-4">
                        <li>• Browsertyp und -version</li>
                        <li>• Betriebssystem</li>
                        <li>• Referrer URL</li>
                        <li>• Hostname des zugreifenden Rechners</li>
                        <li>• Uhrzeit der Serveranfrage</li>
                        <li>• IP-Adresse (ggf. anonymisiert)</li>
                      </ul>
                      <p className="text-gray-700 text-xs sm:text-sm mt-2 leading-relaxed">
                        Diese Informationen sind technisch notwendig, werden nicht mit anderen Datenquellen zusammengeführt und dienen ausschließlich der Sicherstellung des Serverbetriebs.
                      </p>
                    </div>

                    <div className="bg-red-50 rounded-lg p-3 sm:p-4">
                      <h3 className="font-bold text-gray-800 mb-2 text-sm sm:text-base">5. Ihre Rechte</h3>
                      <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
                        Da diese Webanwendung keine personenbezogenen Daten verarbeitet, sind klassische Betroffenenrechte gemäß DSGVO derzeit nicht relevant. Bei Fragen oder Anliegen können Sie mich jederzeit unter der oben genannten E-Mail-Adresse kontaktieren.
                      </p>
                    </div>
                  </div>
                </section>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 p-3 sm:p-4 rounded-b-xl border-t border-gray-200 sticky bottom-0">
                <div className="text-center">
                  <button
                    onClick={() => setShowImpressum(false)}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-4 sm:px-6 py-3 sm:py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 touch-manipulation min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
                  >
                    Schließen
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Footer; 